import {
  KYC_DOCUMENT_REQUEST_NOTE_MAX,
  VERIFICATION_LEVELS,
  deriveVerificationLevel,
  isKycDocumentRequestOpen,
  isVerificationTimestamp,
} from '@carsfinders/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useId, useState } from 'react';

import { ConfirmDialog } from '../../../components/confirm-dialog';
import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { refusalCode } from '../../finance/api';
import { fetchKycStatus, fetchUserDocuments, requestKycDocument } from '../api';
import { type User, type UserDocument } from '../types';
import { cn, formatShortDate } from '../utils';

/** The top rung of the KYC ladder (4 = deposit / exempt). */
const LADDER_TOP = Math.max(...VERIFICATION_LEVELS);

/** The newest `uploadedAt` among the client's documents, or null. */
function lastUploadOf(documents: UserDocument[] | undefined): string | null {
  let latest: string | null = null;
  for (const document of documents ?? []) {
    const at = document.uploadedAt;
    if (at && (!latest || new Date(at).getTime() > new Date(latest).getTime())) latest = at;
  }
  return latest;
}

function riskLabel(score: number): string {
  if (score <= 20) return t('clients.detail.verification.riskLow');
  if (score <= 40) return t('clients.detail.verification.riskMedium');
  return t('clients.detail.verification.riskHigh');
}

function riskClass(score: number): string {
  if (score <= 20) return 'text-cf-success';
  if (score <= 40) return 'text-cf-warn';
  return 'text-cf-danger';
}

/**
 * «Статус верифікації» — the densest card on the artboard.
 *
 * It answers one question in one glance: what is stopping this client from
 * bidding. The percentage and the bar are the summary, the three tiles are the
 * facts an operator quotes on the phone (level, risk, documents on file), the
 * numbered list is the path, and the grey block at the bottom is the actual
 * blocker in words.
 *
 * Every number is computed from what the API serves — GET /users/:id/kyc for
 * risk and the missing/expired lists, GET /users/:id/documents for the count.
 *
 * KYC defects (2026-09-30):
 *  - «Підвищити рівень вручну» is gone. It PATCHed the legacy
 *    `verificationLevel` string, which `UsersService.update` ignores — it
 *    toasted success and changed nothing. The numeric ladder is DERIVED from
 *    facts, so there is nothing to raise. The identity rung is stamped
 *    (`identityVerifiedAt`) only by approving an uploaded document or by the
 *    KYC provider webhook — the header's «Верифікувати» writes the legacy
 *    `users.verified` flag and does not move the ladder.
 *  - «Запросити документ» is a real request (it was a `mailto:` link):
 *    POST /admin/users/:id/kyc/document-request with an optional note. The
 *    artboard's "Клієнту надіслано нагадування 14.09" is now drawn from the
 *    stored request while it is OPEN (`isKycDocumentRequestOpen`).
 */
export function ClientVerificationCard({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const userId = user._id;

  const kycQuery = useQuery({
    queryKey: ['user', userId, 'kyc'],
    queryFn: () => fetchKycStatus(userId as string),
    enabled: !!userId,
    retry: false,
  });
  const documentsQuery = useQuery({
    queryKey: ['user', userId, 'documents'],
    queryFn: () => fetchUserDocuments(userId as string),
    enabled: !!userId,
  });

  const kyc = kycQuery.data;
  const documentCount = documentsQuery.data?.length ?? user.documents?.length ?? 0;
  // BUG-31: the rung and the steps come from the KYC ladder facts
  // (`deriveVerificationLevel`), never from `!!user.phone` or the legacy
  // `verificationLevel` string — a saved but unconfirmed phone is not «Готово».
  const ladder = deriveVerificationLevel(user);

  const steps = [
    { key: 'basic', done: isVerificationTimestamp(user.emailVerified), missing: false },
    { key: 'phone', done: isVerificationTimestamp(user.phoneVerifiedAt), missing: false },
    { key: 'document', done: documentCount > 0, missing: true },
  ] as const;

  const percent = Math.round((ladder / LADDER_TOP) * 100);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [note, setNote] = useState('');
  const noteId = useId();

  const requestOpen = isKycDocumentRequestOpen(user.kycDocumentRequest, {
    identityVerified: isVerificationTimestamp(user.identityVerifiedAt),
    lastDocumentUploadedAt: lastUploadOf(documentsQuery.data ?? user.documents),
  });

  const requestDocument = useMutation({
    mutationFn: () => requestKycDocument(userId as string, note.trim() || undefined),
    onSuccess: () => {
      toast.success(t('clients.detail.verification.requestSent'));
      setDialogOpen(false);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['user', userId] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (error) =>
      toast.error(
        refusalCode(error) === 'IDENTITY_ALREADY_VERIFIED'
          ? t('clients.detail.verification.requestAlreadyVerified')
          : t('clients.detail.verification.requestFailed'),
      ),
  });

  return (
    <section className="flex flex-col gap-3 rounded-cf-lg border border-cf-line bg-cf-surface p-4 lg:gap-4 lg:px-6 lg:py-[22px]">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base leading-[21px] font-semibold tracking-[-0.02em] lg:text-[18px] lg:leading-[23px]">
          <span className="lg:hidden">{t('clients.detail.verification.titleShort')}</span>
          <span className="hidden lg:inline">{t('clients.detail.verification.title')}</span>
        </h2>
        <span data-testid="kyc-percent" className="font-mono text-[13px]">
          {percent}%
        </span>
      </div>

      <div className="h-1.5 overflow-hidden rounded-[3px] bg-cf-fill">
        <div className="h-full bg-cf-accent" style={{ width: `${percent}%` }} />
      </div>

      <div className="grid grid-cols-3 gap-1.5 lg:gap-2.5">
        <Tile label={t('clients.detail.verification.level')}>
          <span data-testid="kyc-ladder-level">
            {t('clients.detail.verification.ladderLevel', { level: ladder, top: LADDER_TOP })}
          </span>
        </Tile>
        <Tile label={t('clients.detail.verification.risk')}>
          {kyc ? (
            <span className={riskClass(kyc.riskScore)}>
              {kyc.riskScore}
              <span className="hidden lg:inline"> · {riskLabel(kyc.riskScore)}</span>
            </span>
          ) : (
            <span className="text-cf-ink-faint">—</span>
          )}
        </Tile>
        <Tile
          label={t('clients.detail.verification.documents')}
          shortLabel={t('clients.detail.verification.documentsShort')}
        >
          {documentCount}
        </Tile>
      </div>

      <ol className="m-0 flex list-none flex-col gap-0.5 p-0">
        {steps.map((step, index) => (
          <li
            key={step.key}
            className="flex items-center gap-2.5 border-b border-cf-line-soft py-2.5 text-sm lg:gap-3"
          >
            <span
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-full font-mono text-[11px]',
                step.done ? 'bg-cf-success-tint text-cf-success' : 'bg-cf-fill text-cf-ink-subtle',
              )}
            >
              {step.done ? '✓' : index + 1}
            </span>
            <span className="flex-1">{t(`clients.detail.verification.steps.${step.key}`)}</span>
            <span className="font-mono text-[11px] text-cf-ink-subtle">
              {step.done ? (
                t('clients.detail.verification.stateDone')
              ) : !step.missing ? (
                t('clients.detail.verification.statePending')
              ) : (
                <>
                  <span className="lg:hidden">
                    {t('clients.detail.verification.stateMissingShort')}
                  </span>
                  <span className="hidden lg:inline">
                    {t('clients.detail.verification.stateMissing')}
                  </span>
                </>
              )}
            </span>
          </li>
        ))}
      </ol>

      <div className="hidden lg:block">
        <BlockerNote kyc={kyc} />
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <button
          type="button"
          data-testid="kyc-request-document"
          onClick={() => setDialogOpen(true)}
          disabled={!userId}
          className="flex h-11 flex-1 items-center justify-center rounded-cf-md bg-cf-brand px-4 text-sm font-semibold text-white hover:bg-cf-brand-hover focus-visible:ring-2 focus-visible:ring-cf-brand focus-visible:ring-offset-2 focus-visible:outline-none disabled:opacity-50 lg:h-10 lg:flex-none"
        >
          {t('clients.detail.verification.requestDocument')}
        </button>
        {requestOpen && user.kycDocumentRequest && (
          <span data-testid="kyc-request-sent" className="font-mono text-[11px] text-cf-ink-subtle">
            {t('clients.detail.verification.requestSentOn', {
              date: formatShortDate(user.kycDocumentRequest.requestedAt),
            })}
          </span>
        )}
      </div>

      <ConfirmDialog
        open={dialogOpen}
        title={t('clients.detail.verification.requestDialogTitle')}
        message={
          <div className="flex flex-col gap-3">
            <p className="m-0">{t('clients.detail.verification.requestDialogBody')}</p>
            <label htmlFor={noteId} className="text-[13px] font-semibold text-cf-ink">
              {t('clients.detail.verification.requestNoteLabel')}
            </label>
            <textarea
              id={noteId}
              data-testid="kyc-request-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={KYC_DOCUMENT_REQUEST_NOTE_MAX}
              rows={3}
              placeholder={t('clients.detail.verification.requestNotePlaceholder')}
              className="w-full resize-y rounded-cf-md border border-cf-line bg-cf-surface px-3 py-2 text-[14px] text-cf-ink focus-visible:ring-2 focus-visible:ring-cf-brand focus-visible:outline-none"
            />
            <span className="self-end font-mono text-[11px] text-cf-ink-subtle">
              {note.length}/{KYC_DOCUMENT_REQUEST_NOTE_MAX}
            </span>
          </div>
        }
        confirmLabel={t('clients.detail.verification.requestConfirm')}
        busy={requestDocument.isPending}
        onConfirm={() => requestDocument.mutate()}
        onCancel={() => setDialogOpen(false)}
      />
    </section>
  );
}

function Tile({
  label,
  shortLabel,
  children,
}: {
  label: string;
  shortLabel?: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-cf-md border border-cf-line-soft p-2.5 lg:px-3.5 lg:py-3">
      <div className="font-mono text-[9px] leading-[12px] tracking-[0.1em] text-cf-ink-subtle uppercase lg:text-[10px] lg:leading-[13px]">
        {shortLabel ? (
          <>
            <span className="lg:hidden">{shortLabel}</span>
            <span className="hidden lg:inline">{label}</span>
          </>
        ) : (
          label
        )}
      </div>
      <div className="mt-[3px] text-[15px] leading-[19px] font-semibold lg:mt-1 lg:text-[18px] lg:leading-[23px]">
        {children}
      </div>
    </div>
  );
}

/**
 * The grey block: what is actually blocking this client, in one sentence.
 *
 * GET /users/:id/kyc answers with machine keys (`identity_document`) and an
 * English recommendation string built in the API. The key is translated here;
 * the recommendation is dropped, because the service only ever emits it
 * alongside the same missing document — printing both would say one fact twice,
 * and the second copy would say it in the wrong language.
 */
function BlockerNote({ kyc }: { kyc?: { missingDocuments: string[]; expiredDocuments: string[] } }) {
  if (!kyc) return null;

  const missing = (kyc.missingDocuments ?? []).map(documentLabel);
  const expired = kyc.expiredDocuments ?? [];

  return (
    <div className="rounded-cf-md bg-cf-ground px-3.5 py-3 text-[13px] leading-[1.5] text-cf-ink-muted">
      {missing.length > 0 && (
        <p className="m-0">
          <strong className="text-cf-ink">{t('clients.detail.verification.missing')}</strong>{' '}
          {missing.join(', ')}
        </p>
      )}
      {expired.length > 0 && (
        <p className={missing.length > 0 ? 'mt-1.5' : 'm-0'}>
          <strong className="text-cf-danger">{t('clients.detail.verification.expired')}</strong>{' '}
          {expired.join(', ')}
        </p>
      )}
      {missing.length === 0 && expired.length === 0 && (
        <p className="m-0">{t('clients.detail.verification.complete')}</p>
      )}
    </div>
  );
}

/** `identity_document` → «документ, що посвідчує особу …»; anything else verbatim. */
function documentLabel(key: string): string {
  if (key === 'identity_document') {
    return t('clients.detail.verification.missingIdentityDocument');
  }
  return key;
}
